import { useEffect, useState } from 'react';

import { ISymbolPriceUpdate } from './type';

type PriceListener = (price: ISymbolPriceUpdate) => void;

class LivePriceEmitter {
  private listeners = new Map<string, Set<PriceListener>>();
  private cache = new Map<string, ISymbolPriceUpdate>();
  private pendingEmits = new Map<string, ISymbolPriceUpdate>();
  private scheduledFlush = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private static FLUSH_INTERVAL_MS = 200;

  emit(symbolId: string, price: ISymbolPriceUpdate) {
    this.cache.set(symbolId, price);
    this.pendingEmits.set(symbolId, price);

    if (!this.scheduledFlush) {
      this.scheduledFlush = true;
      this.flushTimer = setTimeout(() => {
        this.flush();
      }, LivePriceEmitter.FLUSH_INTERVAL_MS);
    }
  }

  private flush() {
    this.pendingEmits.forEach((price, symbolId) => {
      const set = this.listeners.get(symbolId);
      if (set) {
        set.forEach((listener) => listener(price));
      }
    });
    this.pendingEmits.clear();
    this.scheduledFlush = false;
    this.flushTimer = null;
  }

  subscribe(symbolId: string, listener: PriceListener) {
    if (!this.listeners.has(symbolId)) {
      this.listeners.set(symbolId, new Set());
    }
    this.listeners.get(symbolId)!.add(listener);
  }

  unsubscribe(symbolId: string, listener: PriceListener) {
    this.listeners.get(symbolId)?.delete(listener);
  }

  getLastPrice(symbolId: string): ISymbolPriceUpdate | undefined {
    return this.cache.get(symbolId);
  }

  reset() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.listeners.clear();
    this.cache.clear();
    this.pendingEmits.clear();
    this.scheduledFlush = false;
  }
}

export const livePriceEmitter = new LivePriceEmitter();

export function useLivePrice(symbolId: string): ISymbolPriceUpdate | undefined {
  const [price, setPrice] = useState<ISymbolPriceUpdate | undefined>(() => livePriceEmitter.getLastPrice(symbolId));

  useEffect(() => {
    const cached = livePriceEmitter.getLastPrice(symbolId);
    if (cached) {
      setPrice(cached);
    }

    const listener: PriceListener = (update) => setPrice(update);
    livePriceEmitter.subscribe(symbolId, listener);
    return () => livePriceEmitter.unsubscribe(symbolId, listener);
  }, [symbolId]);

  return price;
}
